/**
 * The hangar showcase (Phase 6, BUILD_PLAN section 4): the selected mech on a lit stage behind
 * the roster, the comparison panel with the buy flow, and the loadout editor (modules, paint,
 * decals, parts) with Save and Deploy. Every edit is a draft until the server accepts the save;
 * prices, ownership and levels all come from the API (CLAUDE.md #1).
 */
import { COSMETIC_LIST, getMech, MECH_LIST, MODULE_LIST } from '@steelwake/shared/data';
import { Button, colors, fonts, radii, spacing } from '@steelwake/ui';
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import type { HangarStatus } from '../../game/hangar/HangarHost.js';
import { FrameOverlay } from '../../settings/FrameOverlay.js';
import { useSettingsStore } from '../../settings/store.js';
import { NARROW, useMediaQuery } from '../../useMediaQuery.js';
import { CLASS_LABELS, CLASS_ORDER, MechCard, MechComparePanel } from '../play/MechPicker.js';
import { BuyFlow } from './HangarCatalog.js';
import {
  moduleSummary,
  setChannel,
  toggleModule,
  choosePrimary,
  weaponSummary,
  togglePart,
  type ColourChannel,
  type PaintChannel,
} from './loadout.js';
import { selectDirty, selectDraft, useHangarStore, type HangarTab } from './store.js';
import { useHangarScene } from './useHangarScene.js';

const credits = (n: number) => n.toLocaleString('en-US');
const TABS: { id: HangarTab; label: string }[] = [
  { id: 'loadout', label: 'Loadout' },
  { id: 'paint', label: 'Paint' },
  { id: 'decals', label: 'Decals' },
  { id: 'parts', label: 'Parts' },
];
const PAINT_CHANNELS: { id: PaintChannel; label: string }[] = [
  { id: 'primary', label: 'Primary' },
  { id: 'secondary', label: 'Secondary' },
  { id: 'accent', label: 'Accent' },
];

function describe(status: HangarStatus): string {
  switch (status.phase) {
    case 'probing':
      return 'Probing graphics backend…';
    case 'initialising':
      return `Starting ${status.message === 'webgl2' ? 'WebGL 2' : 'WebGPU'} renderer…`;
    case 'loading':
      return 'Opening the hangar…';
    case 'ready':
      return 'ready';
    case 'error':
      return `Error: ${status.message ?? 'unknown'}`;
  }
}

const panel: React.CSSProperties = {
  position: 'absolute',
  background: 'rgba(11, 13, 16, 0.78)',
  border: `1px solid ${colors.border}`,
  borderRadius: radii.md,
  backdropFilter: 'blur(6px)',
  pointerEvents: 'auto',
  zIndex: 4,
};

const chip = (active: boolean, disabled = false): React.CSSProperties => ({
  padding: '6px 10px',
  background: active ? colors.surface : 'transparent',
  border: `1px solid ${active ? colors.accent : colors.border}`,
  borderRadius: radii.sm,
  color: disabled ? colors.textMuted : colors.text,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: fonts.ui,
  fontSize: 12,
  opacity: disabled ? 0.6 : 1,
});

function Lock({ price, level }: { price: number; level: number | null }) {
  if (level !== null) return <span style={{ color: colors.textMuted }}> level {level}</span>;
  if (price > 0) return <span style={{ color: colors.textMuted }}> {credits(price)} cr</span>;
  return null;
}

export function HangarShowcase({
  onRendererError,
}: {
  onRendererError: (message: string) => void;
}) {
  // Under 800 px the roster becomes a strip along the top and the compare panel steps aside.
  const narrow = useMediaQuery(NARROW);
  const location = useLocation();
  const navigate = useNavigate();
  const view = useHangarStore((s) => s.view);
  const mech = useHangarStore((s) => s.mech);
  const tab = useHangarStore((s) => s.tab);
  const busy = useHangarStore((s) => s.busy);
  const error = useHangarStore((s) => s.error);
  const notice = useHangarStore((s) => s.notice);
  const draft = useHangarStore(selectDraft);
  const dirty = useHangarStore(selectDirty);
  const load = useHangarStore((s) => s.load);
  const select = useHangarStore((s) => s.select);
  const setTab = useHangarStore((s) => s.setTab);
  const edit = useHangarStore((s) => s.edit);
  const revert = useHangarStore((s) => s.revert);
  const save = useHangarStore((s) => s.save);
  const buyItem = useHangarStore((s) => s.buyItem);
  const showStats = useSettingsStore((s) => s.settings.showStats);

  // `?mech=` chooses the first mech, before the scene mounts.
  useLayoutEffect(() => {
    const wanted = new URLSearchParams(location.search).get('mech');
    if (wanted) useHangarStore.getState().select(wanted);
    // Only the first render's address matters.
  }, []);

  const mount = useRef<HTMLDivElement | null>(null);
  const { hostRef, status } = useHangarScene(mount, true, { mech, loadout: draft });

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (status.phase === 'error') onRendererError(status.message ?? 'the renderer failed');
  }, [status, onRendererError]);
  useEffect(() => {
    void hostRef.current?.selectMech(mech, useHangarStore.getState().drafts[mech] ?? null);
  }, [hostRef, mech]);
  useEffect(() => {
    hostRef.current?.setLoadout(draft);
  }, [hostRef, draft]);

  const def = getMech(mech);
  const entries = new Map(view?.mechs.map((m) => [m.id, m]) ?? []);
  const modules = new Map(view?.modules.map((m) => [m.id, m]) ?? []);
  const cosmetics = new Map(view?.cosmetics.map((c) => [c.id, c]) ?? []);
  const owned = (id: string) => cosmetics.get(id)?.owned ?? false;
  const ready = status.phase === 'ready';

  const buyCosmetic = (id: string): ReactNode => {
    const item = cosmetics.get(id);
    if (!item || item.owned || item.price <= 0) return null;
    return (
      <Button
        variant="ghost"
        data-testid={`buy-cosmetic-${id}`}
        onClick={() => void buyItem('cosmetic', id)}
        disabled={busy}
      >
        Buy {credits(item.price)} cr
      </Button>
    );
  };

  const swatches = (channel: ColourChannel, kind: 'paint' | 'emissive') => {
    const prefix = kind === 'paint' ? `paint-${channel}` : 'emissive';
    const current = draft.cosmetics[channel];
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <button
          type="button"
          data-testid={`${prefix}-stock`}
          aria-pressed={current === null}
          onClick={() => edit((d) => setChannel(d, channel, null))}
          style={chip(current === null)}
        >
          Stock
        </button>
        {COSMETIC_LIST.filter((c) => c.kind === kind).map((c) => {
          if (c.kind !== 'paint' && c.kind !== 'emissive') return null;
          const can = owned(c.id);
          return (
            <button
              key={c.id}
              type="button"
              data-testid={`${prefix}-${c.id}`}
              aria-pressed={current === c.id}
              disabled={!can}
              title={`${c.name}${can ? '' : c.levelUnlock !== null ? ` (level ${String(c.levelUnlock)})` : ` (${credits(c.price)} cr)`}`}
              onClick={() => edit((d) => setChannel(d, channel, c.id))}
              style={{
                ...chip(current === c.id, !can),
                width: 26,
                height: 26,
                padding: 0,
                background: c.hex,
                borderWidth: current === c.id ? 2 : 1,
              }}
            >
              <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
                {c.name}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const editor = (): ReactNode => {
    switch (tab) {
      case 'loadout':
        return (
          <div data-testid="loadout-tab" style={{ display: 'grid', gap: spacing.sm }}>
            <div style={{ fontSize: 12, color: colors.textMuted }}>
              Modules {draft.modules.length}/{def.slots.module} · one per group
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
              {MODULE_LIST.map((m) => {
                const entry = modules.get(m.id);
                const can = entry?.owned ?? false;
                const equipped = draft.modules.includes(m.id);
                return (
                  <div key={m.id} style={{ display: 'grid', gap: 4 }}>
                    <button
                      type="button"
                      data-testid={`module-${m.id}`}
                      aria-pressed={equipped}
                      disabled={!can}
                      title={m.description}
                      onClick={() => edit((d, mechDef) => toggleModule(d, m.id, mechDef))}
                      style={chip(equipped, !can)}
                    >
                      {m.name}
                      {!can ? <Lock price={m.price} level={null} /> : null}
                    </button>
                    {!can ? (
                      <Button
                        variant="ghost"
                        data-testid={`buy-module-${m.id}`}
                        onClick={() => void buyItem('module', m.id)}
                        disabled={busy}
                      >
                        Buy {credits(m.price)} cr
                      </Button>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div data-testid="loadout-summary" style={{ fontSize: 12 }}>
              {moduleSummary(draft.modules).join(' · ') || 'No modules equipped.'}
            </div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>Primary weapon</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {[null, ...def.alternates.primary].map((id) => {
                const stock = def.weapons.find((w) => w.slot === 'primary')?.id ?? '';
                const weaponId = id ?? stock;
                const equipped = draft.weapons.primary === id;
                return (
                  <button
                    key={weaponId}
                    type="button"
                    data-testid={id === null ? 'weapon-stock' : `weapon-${id}`}
                    aria-pressed={equipped}
                    title={weaponSummary(weaponId)}
                    onClick={() => edit((d, mechDef) => choosePrimary(d, id, mechDef))}
                    style={chip(equipped)}
                  >
                    {weaponSummary(weaponId)}
                    {id === null ? ' (stock)' : ''}
                  </button>
                );
              })}
            </div>
          </div>
        );
      case 'paint':
        return (
          <div data-testid="paint-tab" style={{ display: 'grid', gap: spacing.sm }}>
            {PAINT_CHANNELS.map((ch) => (
              <div key={ch.id} style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8 }}>
                <span style={{ fontSize: 12, color: colors.textMuted }}>{ch.label}</span>
                {swatches(ch.id, 'paint')}
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8 }}>
              <span style={{ fontSize: 12, color: colors.textMuted }}>Glow</span>
              {swatches('emissive', 'emissive')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {COSMETIC_LIST.filter(
                (c) => (c.kind === 'paint' || c.kind === 'emissive') && !owned(c.id) && c.price > 0,
              ).map((c) => (
                <span key={c.id}>{buyCosmetic(c.id)}</span>
              ))}
            </div>
          </div>
        );
      case 'decals':
        return (
          <div data-testid="decals-tab" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button
              type="button"
              data-testid="decal-none"
              aria-pressed={draft.cosmetics.decal === null}
              onClick={() => edit((d) => setChannel(d, 'decal', null))}
              style={chip(draft.cosmetics.decal === null)}
            >
              None
            </button>
            {COSMETIC_LIST.filter((c) => c.kind === 'decal').map((c) => {
              const can = owned(c.id);
              return (
                <span key={c.id} style={{ display: 'inline-flex', gap: 4 }}>
                  <button
                    type="button"
                    data-testid={`decal-${c.id}`}
                    aria-pressed={draft.cosmetics.decal === c.id}
                    disabled={!can}
                    onClick={() => edit((d) => setChannel(d, 'decal', c.id))}
                    style={chip(draft.cosmetics.decal === c.id, !can)}
                  >
                    {c.name}
                    {!can ? <Lock price={c.price} level={c.levelUnlock} /> : null}
                  </button>
                  {buyCosmetic(c.id)}
                </span>
              );
            })}
          </div>
        );
      case 'parts':
        return (
          <div data-testid="parts-tab" style={{ display: 'grid', gap: spacing.sm }}>
            <div style={{ fontSize: 12, color: colors.textMuted }}>
              Extra parts {draft.cosmetics.parts.length}/{def.slots.cosmetic} · the stock parts stay
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {COSMETIC_LIST.filter((c) => c.kind === 'part' && c.chassis === mech).map((c) => {
                const can = owned(c.id);
                const equipped = draft.cosmetics.parts.includes(c.id);
                return (
                  <span key={c.id} style={{ display: 'inline-flex', gap: 4 }}>
                    <button
                      type="button"
                      data-testid={`part-${c.id}`}
                      aria-pressed={equipped}
                      disabled={!can}
                      onClick={() => edit((d, mechDef) => togglePart(d, c.id, mechDef))}
                      style={chip(equipped, !can)}
                    >
                      {c.name}
                      {!can ? <Lock price={c.price} level={c.levelUnlock} /> : null}
                    </button>
                    {buyCosmetic(c.id)}
                  </span>
                );
              })}
            </div>
          </div>
        );
    }
  };

  return (
    <div
      data-testid="hangar"
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        background: colors.background,
        fontFamily: fonts.ui,
        color: colors.text,
      }}
    >
      <div ref={mount} data-testid="hangar-mount" style={{ position: 'absolute', inset: 0 }} />

      <div
        data-testid="hangar-status"
        data-phase={status.phase}
        role="status"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '10px 16px',
          background: 'rgba(11, 13, 16, 0.8)',
          border: `1px solid ${status.phase === 'error' ? colors.danger : colors.border}`,
          borderRadius: radii.md,
          fontSize: 14,
          opacity: ready ? 0 : 1,
          transition: 'opacity 300ms',
          pointerEvents: 'none',
          zIndex: 5,
        }}
      >
        {describe(status)}
      </div>

      <aside
        data-testid="hangar-roster"
        data-stacked={narrow ? 'true' : 'false'}
        style={{
          ...panel,
          padding: spacing.sm,
          ...(narrow
            ? {
                left: 8,
                right: 8,
                top: 8,
                width: 'auto',
                maxHeight: 150,
                overflowX: 'auto',
                overflowY: 'auto',
                display: 'flex',
                gap: spacing.md,
                alignItems: 'flex-start',
              }
            : { left: 12, top: 12, bottom: 232, width: 210, overflowY: 'auto' }),
        }}
      >
        <div style={{ fontSize: 12, letterSpacing: '0.16em', margin: '4px 0 8px' }}>
          HANGAR ·{' '}
          <strong data-testid="hangar-credits" style={{ color: colors.text }}>
            {view ? credits(view.balances.credits) : '…'} cr
          </strong>
          {view ? <span style={{ color: colors.textMuted }}> · level {view.level}</span> : null}
        </div>
        {CLASS_ORDER.map((cls) => (
          <section key={cls} style={{ marginBottom: spacing.sm }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: colors.textMuted,
                margin: '4px 0',
              }}
            >
              {CLASS_LABELS[cls]}
            </div>
            <div style={{ display: 'grid', gap: 4 }}>
              {MECH_LIST.filter((m) => m.class === cls).map((m) => {
                const e = entries.get(m.id);
                const note = !e ? null : e.starter ? (
                  <span style={{ color: colors.accent }}>starter</span>
                ) : e.owned ? (
                  <span style={{ color: colors.success }}>owned</span>
                ) : e.price !== null ? (
                  <span style={{ color: colors.textMuted }}>{credits(e.price)} cr</span>
                ) : null;
                return (
                  <MechCard
                    key={m.id}
                    mech={m}
                    active={m.id === mech}
                    onSelect={select}
                    note={note}
                    compact
                  />
                );
              })}
            </div>
          </section>
        ))}
      </aside>

      <div
        data-testid="hangar-compare"
        style={{
          ...panel,
          right: 12,
          top: 12,
          bottom: 232,
          width: 340,
          overflowY: 'auto',
          background: 'transparent',
          border: 'none',
          backdropFilter: 'none',
          display: narrow ? 'none' : 'block',
        }}
      >
        <MechComparePanel mech={def} footer={<BuyFlow />} sticky={false} />
      </div>

      <section
        data-testid="hangar-editor"
        style={{ ...panel, left: 12, right: 12, bottom: 12, height: 208, padding: spacing.md }}
      >
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: spacing.sm }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`tab-${t.id}`}
              aria-pressed={tab === t.id}
              onClick={() => setTab(t.id)}
              style={chip(tab === t.id)}
            >
              {t.label}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          {dirty ? (
            <span data-testid="loadout-dirty" style={{ fontSize: 12, color: colors.accent }}>
              Unsaved changes
            </span>
          ) : null}
          {dirty ? (
            <Button variant="ghost" data-testid="revert-loadout" onClick={revert} disabled={busy}>
              Revert
            </Button>
          ) : null}
          <Button data-testid="save-loadout" onClick={() => void save()} disabled={!dirty || busy}>
            Save
          </Button>
          <Button
            variant="ghost"
            data-testid="deploy"
            onClick={() => void navigate(`/play/online?mech=${mech}`)}
            disabled={dirty || !(entries.get(mech)?.owned ?? false)}
            title={dirty ? 'Save the loadout first' : 'Queue with this mech'}
          >
            Deploy
          </Button>
          <Link to="/" data-testid="leave-hangar" style={{ color: colors.textMuted, fontSize: 12 }}>
            Leave
          </Link>
        </div>
        <div style={{ height: 128, overflowY: 'auto' }}>{editor()}</div>
        {notice && tab !== 'loadout' ? null : null}
        {error ? (
          <p
            data-testid="loadout-error"
            role="alert"
            style={{ color: colors.danger, fontSize: 12, margin: '6px 0 0' }}
          >
            {error}
          </p>
        ) : notice ? (
          <p
            data-testid="loadout-notice"
            role="status"
            style={{ color: colors.success, fontSize: 12, margin: '6px 0 0' }}
          >
            {notice}
          </p>
        ) : null}
      </section>

      {showStats ? <FrameOverlay gameRef={hostRef} /> : null}
    </div>
  );
}
